import { useEffect, useState } from "react";
import { User as UserIcon, KeyRound, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "./Dashboard";
import { ROLE_BADGE_COLORS } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "../components/StatusBadge";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [activity, setActivity] = useState(null);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/auth/activity").then((r) => setActivity(r.data)).catch(() => setActivity({ last_logins: [] }));
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) {
      toast.error("La confirmation ne correspond pas.");
      return;
    }
    setSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      const me = await API.get("/auth/me");
      setUser(me.data);
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("Mot de passe modifie avec succes.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold">Mon compte</h1>
        <p className="text-sm text-muted-foreground">Informations du compte, securite et dernieres connexions.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="profile-info">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-[#009E60]" /> Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#009E60] text-white text-xl font-bold">
                {(user?.first_name?.[0] || "U").toUpperCase()}
              </span>
              <div>
                <p className="font-heading font-bold text-lg">{user?.first_name} {user?.last_name}</p>
                <Badge className={ROLE_BADGE_COLORS[user?.role] || ""}>{user?.role_name}</Badge>
              </div>
            </div>
            <div className="text-sm space-y-1.5 pt-2">
              <p><span className="text-muted-foreground">Identifiant :</span> <span className="mono">{user?.username}</span></p>
              <p><span className="text-muted-foreground">Email :</span> {user?.email}</p>
              <p><span className="text-muted-foreground">Service :</span> {user?.service || "-"}</p>
              <p><span className="text-muted-foreground">Derniere connexion :</span> {user?.last_login_at ? fmtDate(user.last_login_at) : "-"}</p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="profile-password">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#3A75C4]" /> Changer mon mot de passe
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="space-y-3" data-testid="profile-password-form">
              <Input type="password" placeholder="Mot de passe actuel" value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })} required data-testid="profile-pwd-current" />
              <Input type="password" placeholder="Nouveau mot de passe" value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })} required data-testid="profile-pwd-new" />
              <Input type="password" placeholder="Confirmation" value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required data-testid="profile-pwd-confirm" />
              <p className="text-[11px] text-muted-foreground">Minimum 10 caracteres : majuscule, minuscule, chiffre, caractere special. Les 5 derniers mots de passe ne peuvent pas etre reutilises.</p>
              <Button type="submit" disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="profile-pwd-submit">
                {saving ? "Enregistrement..." : "Modifier le mot de passe"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="profile-activity">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-[#3A75C4]" /> Dernieres connexions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!activity ? <Skeleton className="h-32 w-full" /> : activity.last_logins.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune connexion enregistree.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-accent/60 text-left">
                  <tr>{["Date", "Adresse IP", "Navigateur", "Resultat"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {activity.last_logins.map((l, i) => (
                    <tr key={i} className="border-t" data-testid={`login-history-${i}`}>
                      <td className="px-3 py-2 text-xs mono whitespace-nowrap">{fmtDate(l.date)}</td>
                      <td className="px-3 py-2 text-xs mono">{l.ip || "-"}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-56 truncate">{l.user_agent || "-"}</td>
                      <td className="px-3 py-2"><StatusBadge value={l.result} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            Si vous constatez une connexion inhabituelle, contactez immediatement votre administrateur. Une adresse IP seule ne permet pas d'identifier formellement une personne.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
